
// Simple memory leak detection utility

// Track component render counts to detect potential memory leaks
const componentRenderCounts = new Map<string, number>();
const componentInstances = new Map<string, Set<unknown>>();
const renderTimes = new Map<string, number[]>();

// Thresholds for leak detection
const RENDER_COUNT_THRESHOLD = 50; // Suspicious if rendered more than this many times
const INSTANCE_COUNT_THRESHOLD = 20; // Suspicious if more than this many instances
const TIME_INCREASE_THRESHOLD = 1.5; // Suspicious if render time increases by this factor

/**
 * Record a component render for memory leak detection
 */
export function recordComponentRender(
  componentName: string, 
  renderTime: number, 
  instanceId: string
): void {
  // Update render count
  const currentCount = componentRenderCounts.get(componentName) || 0;
  componentRenderCounts.set(componentName, currentCount + 1);
  
  // Track render times
  const times = renderTimes.get(componentName) || [];
  times.push(renderTime);
  if (times.length > 10) times.shift();
  renderTimes.set(componentName, times);
  
  // Track component instances
  if (!componentInstances.has(componentName)) {
    componentInstances.set(componentName, new Set());
  }
  componentInstances.get(componentName)?.add(instanceId);
}

/**
 * Clear tracking data for a component
 */
export function clearComponentTracking(componentName: string): void {
  componentRenderCounts.delete(componentName);
  componentInstances.delete(componentName);
  renderTimes.delete(componentName);
}

/**
 * Get suspected memory leaks based on component render patterns
 */
export function getSuspectedLeaks(): Map<string, { 
  renderCount: number; 
  instanceCount: number; 
  averageRenderTime: number;
  trend: 'increasing' | 'stable' | 'unknown';
}> {
  const suspectedLeaks = new Map();
  
  componentRenderCounts.forEach((renderCount, componentName) => {
    const instances = componentInstances.get(componentName)?.size || 0;
    const times = renderTimes.get(componentName) || [];
    
    // Calculate average render time
    const avgRenderTime = times.length > 0 
      ? times.reduce((sum, time) => sum + time, 0) / times.length
      : 0;
    
    // Check render time trend
    let trend: 'increasing' | 'stable' | 'unknown' = 'unknown';
    if (times.length >= 5) {
      const firstHalf = times.slice(0, Math.floor(times.length / 2));
      const secondHalf = times.slice(Math.floor(times.length / 2));
      
      const firstAvg = firstHalf.reduce((sum, time) => sum + time, 0) / firstHalf.length;
      const secondAvg = secondHalf.reduce((sum, time) => sum + time, 0) / secondHalf.length;
      
      trend = secondAvg > firstAvg * TIME_INCREASE_THRESHOLD ? 'increasing' : 'stable';
    }
    
    // Check for suspicious patterns
    if (renderCount > RENDER_COUNT_THRESHOLD || instances > INSTANCE_COUNT_THRESHOLD) {
      suspectedLeaks.set(componentName, {
        renderCount,
        instanceCount: instances,
        averageRenderTime: avgRenderTime,
        trend
      });
    }
  });
  
  return suspectedLeaks;
}

/**
 * Check for memory leaks in the application
 */
function checkForMemoryLeaks(): void {
  const leaks = getSuspectedLeaks();
  
  if (leaks.size > 0) {
    console.warn('Potential memory leaks detected in the following components:');
    leaks.forEach((data, component) => {
      console.warn(
        `${component}: ${data.renderCount} renders, ${data.instanceCount} instances, ` +
        `${data.averageRenderTime.toFixed(2)}ms average render time (${data.trend} trend)`
      );
    });
  }
}

export default checkForMemoryLeaks;
